import { useEffect, useRef, useState } from 'react'
import type { DraaitafelConfig } from './types'

/**
 * De draaitafel van Linde: studiorenders om de 10°, die meedraaien als je
 * sleept en langzaam vanzelf ronddraaien als niemand iets doet.
 *
 * Dit is geen benadering van een model maar het omgekeerde: het zijn Linde's
 * eigen renders van hun CAD-model, dus op dit scherm is de machine echt. Wat
 * ze niet kunnen, is bewegen of van boven bekeken worden — daarvoor is er het
 * GLB-model (`modelUrl`), dat voorgaat zodra het er is.
 *
 * Alle frames worden vooraf geladen en als gestapelde <img> getoond, waarvan
 * er één zichtbaar is (opacity, zie viewer.css). Na het laden worden ze
 * alvast gedecodeerd; wisselen is dan een klassewissel, ook op de mini-pc.
 * Met één <img> en een wisselende `src` knippert het bij snel slepen.
 */
export function Draaitafel({ config, onGeladen, draait }: {
  config: DraaitafelConfig
  onGeladen: () => void
  /** Vanzelf ronddraaien als niemand sleept. */
  draait: boolean
}) {
  const n = config.frames.length
  const [index, zetIndex] = useState(() => Math.max(0, config.frames.indexOf(config.start ?? config.frames[0])))
  const [klaar, zetKlaar] = useState(0)
  const sleep = useRef<{ x: number; index: number; t: number } | null>(null)
  const laatsteAanraking = useRef(0)
  const vlak = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (klaar === n) onGeladen()
  }, [klaar, n, onGeladen])

  // Vanzelf draaien: standaard een frame per 110 ms (bij 36 frames een
  // omwenteling per vier seconden), pas drie seconden na de laatste
  // aanraking, zodat hij niet onder je vinger wegdraait.
  const stapMs = config.stapMs ?? 110
  useEffect(() => {
    if (!draait || klaar < n) return
    const id = window.setInterval(() => {
      if (sleep.current || performance.now() - laatsteAanraking.current < 3000) return
      zetIndex((i) => (i + 1) % n)
    }, stapMs)
    return () => window.clearInterval(id)
  }, [draait, klaar, n, stapMs])

  // Een frame per zoveel pixels slepen: de hele omwenteling over ongeveer
  // anderhalve keer de breedte van het vlak, zoals een draaischijf aanvoelt.
  const pxPerFrame = () => ((vlak.current?.clientWidth ?? 1200) * 1.5) / n

  const neer = (e: React.PointerEvent) => {
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    sleep.current = { x: e.clientX, index, t: performance.now() }
    laatsteAanraking.current = performance.now()
  }
  const beweeg = (e: React.PointerEvent) => {
    const s = sleep.current
    if (!s) return
    // Naar links slepen draait de machine linksom, net als een echte schijf.
    const stap = Math.round((s.x - e.clientX) / pxPerFrame())
    zetIndex((((s.index + stap) % n) + n) % n)
    laatsteAanraking.current = performance.now()
  }
  const los = () => {
    sleep.current = null
    laatsteAanraking.current = performance.now()
  }

  return (
    <div
      ref={vlak}
      className={`pv-draai${klaar === n ? ' pv-aan' : ''}${config.overvloeien ? ' pv-draai-vloei' : ''}`}
      onPointerDown={neer}
      onPointerMove={beweeg}
      onPointerUp={los}
      onPointerCancel={los}
      role="img"
      aria-label="360°"
    >
      {config.frames.map((f, i) => (
        <img
          key={f}
          src={`${config.map}${String(f).padStart(2, '0')}.webp`}
          className={i === index ? 'pv-draai-zicht' : undefined}
          alt=""
          draggable={false}
          onLoad={(e) => {
            // Alvast decoderen, zodat het frame klaarstaat als het aan de
            // beurt is; tellen doet hij pas daarna.
            const img = e.currentTarget
            img.decode().catch(() => undefined).finally(() => zetKlaar((k) => k + 1))
          }}
          onError={() => zetKlaar((k) => k + 1)}
        />
      ))}
    </div>
  )
}
